import { getGlobalSettings } from '../../../utils/global_settings';
import { yesterday, today, subYears, addDays } from '../../../utils/date';
import { generateDateTimeSuffix } from '../../../utils/helpers';
import { PUBLIC_PANEL_VERSIONS, PANEL_ALLOWED_COUNTRIES } from '../../../utils/advanced-metrics';

export const { permissions } = getGlobalSettings();

const allowSchedulingProgress = permissions.allowCustomExportSchedulingProgress;

export const ADVANCED_METRIC_MIN_DATE = '2022-01-01';
export const MPI_MIN_DATE = '2022-01-01';
export const SCHEDULED_EXPORTS_LIMIT = 6;

export const DATA_TYPE = {
  BASE: 'base',
  ADVANCED: 'advanced',
  MPI: 'mpi',
};

export const RENDER_FIELDS = {
  [DATA_TYPE.BASE]: [
    'data_points',
    'countries_iso',
    'growth_rates',
    ...(permissions.allowAdvancedExportModal ? ['use_entity_relations'] : []),
    'geo_granularity',
    'data_granularity',
    'data_schema',
    'output_format',
    'export_type',
    ...(allowSchedulingProgress ? ['email_notification'] : []),
  ],
  [DATA_TYPE.ADVANCED]: [
    'data_points',
    'user_segments',
    ...(permissions.allowAdvancedExportModal ? ['growth_rates'] : []),
    'geo_granularity',
    'data_granularity',
    'data_schema',
    'output_format',
    'export_type',
    ...(allowSchedulingProgress ? ['email_notification'] : []),
  ],
  [DATA_TYPE.MPI]: [
    'data_granularity',
    'output_format',
    ...(permissions.allowAdvancedExportModal ? ['growth_rates'] : []),
    'export_type',
    ...(allowSchedulingProgress ? ['email_notification'] : []),
  ],
};

export const FIELDS_TO_VALIDATE = {
  [DATA_TYPE.BASE]: ['name', 'entities', 'start_date', 'end_date', 'data_points', 'countries_iso'],
  [DATA_TYPE.ADVANCED]: [
    'name',
    'entities',
    'start_date',
    'end_date',
    'data_points',
    'user_segments',
    'reportEntities',
  ],
  [DATA_TYPE.MPI]: ['name', 'entities', 'start_date', 'end_date', 'reportEntities'],
};

export const SETTINGS_FIELDS = {
  [DATA_TYPE.BASE]: [
    'data_points',
    'growth_rates',
    ...(permissions.allowAdvancedExportModal ? ['use_entity_relations'] : []),
    'countries_iso',
    'include_ww',
    'geo_granularity',
    'data_granularity',
    'data_schema',
    'split_by_acquisition',
    'output_format',
    ...(allowSchedulingProgress ? ['email_notification'] : []),
  ],
  [DATA_TYPE.ADVANCED]: [
    'data_points',
    ...(permissions.allowAdvancedExportModal ? ['growth_rates'] : []),
    'user_segments',
    'countries_iso',
    'geo_granularity',
    'data_granularity',
    'data_schema',
    'output_format',
    ...(allowSchedulingProgress ? ['email_notification'] : []),
  ],
  [DATA_TYPE.MPI]: [
    ...(permissions.allowAdvancedExportModal ? ['growth_rates'] : []),
    'data_granularity',
    'output_format',
    'mpi_data_points',
    ...(allowSchedulingProgress ? ['email_notification'] : []),
  ],
};

const DEFAULT_DATES = {
  start_date: addDays(subYears(today(), 2), 1),
  end_date: yesterday(),
};

const defaultReportName = generateDateTimeSuffix();

export const DEFAULT_REPORT = {
  [DATA_TYPE.BASE]: {
    id: null,
    name: defaultReportName,
    entities: [],
    ...DEFAULT_DATES,
    data_type: DATA_TYPE.BASE,
    data_points: ['downloads'],
    ...(permissions.allowAdvancedExportModal
      ? {
          growth_rates: [],
        }
      : { growth_rates: ['m-over-m'] }),
    countries_iso: ['US'],
    include_ww: false,
    geo_granularity: 'country',
    use_entity_relations: permissions.allowAdvancedExportModal ? true : undefined,
    data_granularity: 'daily',
    data_schema: 'parent_company_unified',
    split_by_acquisition: false,
    output_format: 'horizontal',
    export_type: 'xls',
    panel_version: PUBLIC_PANEL_VERSIONS[permissions.advancedDatasets[0]],
    ...(allowSchedulingProgress ? { email_notification: true } : {}),
  },
  [DATA_TYPE.ADVANCED]: {
    id: null,
    name: defaultReportName,
    entities: [],
    ...DEFAULT_DATES,
    data_type: DATA_TYPE.ADVANCED,
    ...(permissions.allowAdvancedExportModal
      ? {
          growth_rates: [],
        }
      : {}),
    data_points: ['time_spent_penetration'],
    user_segments: ['all_users'], // all_users
    countries_iso: PANEL_ALLOWED_COUNTRIES[permissions.advancedDatasets[0]],
    geo_granularity: 'country',
    data_granularity: 'daily',
    data_schema: 'app_unified',
    output_format: 'horizontal',
    export_type: 'csv',
    panel_version: PUBLIC_PANEL_VERSIONS[permissions.advancedDatasets[0]],
    cross_apps_pairs: {},
    ...(allowSchedulingProgress ? { email_notification: true } : {}),
  },
  [DATA_TYPE.MPI]: {
    id: null,
    name: defaultReportName,
    countries_iso: [],
    entities: [],
    data_points: [],
    ...DEFAULT_DATES,
    data_type: DATA_TYPE.MPI,
    ...(permissions.allowAdvancedExportModal
      ? {
          growth_rates: [],
        }
      : {}),
    data_granularity: 'daily',
    output_format: 'horizontal',
    geo_granularity: 'country', // region
    data_schema: 'parent_company_unified',
    export_type: 'csv',
    panel_version: PUBLIC_PANEL_VERSIONS[permissions.advancedDatasets[0]],
    mpi_data_points: [],
    ...(allowSchedulingProgress ? { email_notification: true } : {}),
  },
};
